import { useEffect, useState } from "react";
import { Link, Outlet, useParams, useRouterState } from "@tanstack/react-router";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CircleAlert,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  GripVertical,
  ImageIcon,
  MoreHorizontal,
  Paintbrush,
  Pencil,
  Plus,
  Rocket,
  Snowflake,
  Trash2,
} from "lucide-react";
import { ALLERGENS, DIETARY_TAGS, minorToDecimalString, parseMoneyToMinor, type Allergen, type CategoryDto, type DietaryTag, type ItemDto, type ModifierGroupDto } from "@dishfolio/contracts";
import { allergenLabel, dietaryLabel } from "@dishfolio/i18n";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Field,
  Input,
  Label,
  NativeSelect,
  Sheet,
  Skeleton,
  Switch,
  Textarea,
  cn,
  toast,
} from "@dishfolio/ui";
import { api } from "../lib/api.ts";
import { useOrg } from "../lib/org.tsx";
import { money } from "../lib/format.ts";
import { useContent, useInvalidateMenu, useMenu, useRestaurants } from "../lib/queries.ts";
import { PageContainer } from "../components/Page.tsx";
import { useMediaPicker } from "../components/MediaPicker.tsx";
import { notifyError } from "../components/Upgrade.tsx";
import { MenuStatus } from "./menus.tsx";
import { PublishDialog } from "./menu-publish.tsx";

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const TABS = [
  { to: "/menus/$menuId", label: "Content", exact: true },
  { to: "/menus/$menuId/translations", label: "Translations" },
  { to: "/menus/$menuId/publish", label: "Publish & QR" },
  { to: "/menus/$menuId/analytics", label: "Analytics" },
  { to: "/menus/$menuId/settings", label: "Settings" },
];

export function MenuLayout() {
  const { menuId } = useParams({ from: "/app/menus/$menuId" });
  const menu = useMenu(menuId);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { can } = useOrg();
  const [publishOpen, setPublishOpen] = useState(false);
  if (menu.isPending) return <PageContainer wide><Skeleton className="h-24" /></PageContainer>;
  if (menu.error || !menu.data) return <PageContainer><EmptyState title="Menu not found" /></PageContainer>;
  const m = menu.data.menu;
  return (
    <div className="flex min-h-full flex-col">
      <div className="border-b bg-card">
        <div className="mx-auto w-full max-w-7xl px-4 pt-6 sm:px-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <Link to="/menus" className="text-xs font-medium text-muted-foreground hover:text-foreground">
                ← Menus
              </Link>
              <h1 className="mt-1 flex items-center gap-3 font-display text-2xl font-semibold">
                {m.name} <MenuStatus menu={m} />
              </h1>
              <a href={m.publicUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary">
                {m.publicUrl.replace(/^https?:\/\//, "")} <ExternalLink className="size-3" />
              </a>
            </div>
            <div className="flex flex-wrap gap-2">
              {can("menu:edit") ? (
                <Button asChild variant="outline">
                  <Link to="/menus/$menuId/design" params={{ menuId }}>
                    <Paintbrush /> Design
                  </Link>
                </Button>
              ) : null}
              {can("menu:publish") ? (
                <Button onClick={() => setPublishOpen(true)} disabled={Boolean(menu.data.frozenReason)}>
                  <Rocket /> {m.status === "published" ? "Publish changes" : "Publish"}
                </Button>
              ) : null}
            </div>
          </div>
          <nav className="mt-5 -mb-px flex gap-1 overflow-x-auto" aria-label="Menu sections">
            {TABS.map((t) => {
              const href = t.to.replace("$menuId", menuId);
              const active = t.exact ? path === href || path === `${href}/` : path.startsWith(href);
              return (
                <Link
                  key={t.to}
                  to={t.to as never}
                  params={{ menuId } as never}
                  className={cn("border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition", active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground")}
                >
                  {t.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
      {menu.data.frozenReason ? (
        <div className="border-b bg-destructive/5">
          <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-2 text-sm sm:px-8">
            <Snowflake className="size-4 text-destructive" /> This menu is read-only because it is {menu.data.frozenReason}. It stays online for guests.{" "}
            <Link to="/billing" className="font-medium text-primary hover:underline">
              Upgrade
            </Link>
          </div>
        </div>
      ) : null}
      <div className="flex-1">
        <Outlet />
      </div>
      <PublishDialog menuId={menuId} open={publishOpen} onOpenChange={setPublishOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Item editor
// ---------------------------------------------------------------------------

type ItemForm = {
  name: string;
  description: string;
  price: string;
  imageAssetId: string | null;
  imageUrl: string | null;
  allergens: Allergen[];
  dietaryTags: DietaryTag[];
  isAvailable: boolean;
  isVisible: boolean;
  categoryId: string;
  variants: Array<{ id?: string; name: string; price: string }>;
  taxRate: string;
};

function toForm(item: ItemDto | null, categoryId: string, currency: string): ItemForm {
  return {
    name: item?.name ?? "",
    description: item?.description ?? "",
    price: item?.priceMinor != null ? minorToDecimalString(item.priceMinor, currency) : "",
    imageAssetId: item?.imageAssetId ?? null,
    imageUrl: item?.imageUrl ?? null,
    allergens: item?.allergens ?? [],
    dietaryTags: item?.dietaryTags ?? [],
    isAvailable: item?.isAvailable ?? true,
    isVisible: item?.isVisible ?? true,
    categoryId,
    variants: item?.variants.map((v) => ({ id: v.id, name: v.name, price: minorToDecimalString(v.priceMinor, currency) })) ?? [],
    taxRate: item?.taxRateBps != null ? String(item.taxRateBps / 100) : "",
  };
}

function ModifierGroupsEditor({ item, onChanged }: { item: ItemDto; onChanged: () => void }) {
  const { org } = useOrg();
  const [editing, setEditing] = useState<ModifierGroupDto | "new" | null>(null);
  const [form, setForm] = useState<{ name: string; minSelect: number; maxSelect: number; options: Array<{ id?: string; name: string; price: string; isAvailable: boolean }> }>({ name: "", minSelect: 0, maxSelect: 1, options: [] });
  const restaurants = useRestaurants();
  const currency = restaurants.data?.[0]?.currency ?? "EUR";
  const open = (g: ModifierGroupDto | "new") => {
    setEditing(g);
    setForm(
      g === "new"
        ? { name: "", minSelect: 0, maxSelect: 1, options: [{ name: "", price: "0", isAvailable: true }] }
        : { name: g.name, minSelect: g.minSelect, maxSelect: g.maxSelect, options: g.options.map((o) => ({ id: o.id, name: o.name, price: minorToDecimalString(o.priceDeltaMinor, currency), isAvailable: o.isAvailable })) },
    );
  };
  const save = async () => {
    const body = {
      name: form.name,
      minSelect: form.minSelect,
      maxSelect: Math.max(form.minSelect, form.maxSelect, 1),
      options: form.options.filter((o) => o.name.trim()).map((o) => ({ id: o.id, name: o.name, priceDeltaMinor: parseMoneyToMinor(o.price || "0", currency) ?? 0, isAvailable: o.isAvailable })),
    };
    try {
      if (editing === "new") await api.post(`/items/${item.id}/modifier-groups`, body);
      else if (editing) await api.put(`/modifier-groups/${editing.id}`, body);
      setEditing(null);
      onChanged();
    } catch (err) {
      notifyError(err);
    }
  };
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Label>Choices & extras</Label>
        <Button variant="ghost" size="sm" onClick={() => open("new")}>
          <Plus /> Add group
        </Button>
      </div>
      {!org?.plan.tableOrdering ? <p className="text-xs text-muted-foreground">Modifiers are shown to guests and used for table ordering (Business plan).</p> : null}
      {item.modifierGroups.map((g) => (
        <div key={g.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
          <div>
            <div className="font-medium">{g.name}</div>
            <div className="text-xs text-muted-foreground">
              {g.minSelect > 0 ? `Required, choose ${g.minSelect}–${g.maxSelect}` : `Optional, up to ${g.maxSelect}`} · {g.options.map((o) => o.name).join(", ")}
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon-sm" onClick={() => open(g)} aria-label="Edit group">
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Delete group"
              onClick={async () => {
                await api.del(`/modifier-groups/${g.id}`);
                onChanged();
              }}
            >
              <Trash2 />
            </Button>
          </div>
        </div>
      ))}
      {editing ? (
        <div className="grid gap-3 rounded-lg border bg-muted/40 p-3">
          <Field label="Group name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Size, Extras, Sauce…" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Minimum choices" hint="0 = optional">
              <Input type="number" min={0} max={20} value={form.minSelect} onChange={(e) => setForm({ ...form, minSelect: Number(e.target.value) })} />
            </Field>
            <Field label="Maximum choices">
              <Input type="number" min={1} max={20} value={form.maxSelect} onChange={(e) => setForm({ ...form, maxSelect: Number(e.target.value) })} />
            </Field>
          </div>
          <div className="grid gap-2">
            {form.options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={o.name} placeholder="Option" onChange={(e) => setForm({ ...form, options: form.options.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                <Input className="w-24" value={o.price} inputMode="decimal" placeholder="+0.00" onChange={(e) => setForm({ ...form, options: form.options.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)) })} />
                <Button variant="ghost" size="icon-sm" onClick={() => setForm({ ...form, options: form.options.filter((_, j) => j !== i) })} aria-label="Remove option">
                  <Trash2 />
                </Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="justify-self-start" onClick={() => setForm({ ...form, options: [...form.options, { name: "", price: "0", isAvailable: true }] })}>
              <Plus /> Option
            </Button>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={!form.name.trim() || !form.options.some((o) => o.name.trim())}>
              Save group
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ItemSheet({ menuId, item, categoryId, categories, currency, onClose }: { menuId: string; item: ItemDto | null; categoryId: string; categories: CategoryDto[]; currency: string; onClose: () => void }) {
  const [form, setForm] = useState<ItemForm>(() => toForm(item, categoryId, currency));
  const [saving, setSaving] = useState(false);
  const invalidate = useInvalidateMenu(menuId);
  const picker = useMediaPicker();
  const set = (patch: Partial<ItemForm>) => setForm((f) => ({ ...f, ...patch }));
  const priceMinor = form.price.trim() ? parseMoneyToMinor(form.price, currency) : null;
  const priceInvalid = form.price.trim() !== "" && priceMinor == null;
  const save = async (markReviewed = false) => {
    setSaving(true);
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      priceMinor,
      imageAssetId: form.imageAssetId,
      allergens: form.allergens,
      dietaryTags: form.dietaryTags,
      isAvailable: form.isAvailable,
      variants: form.variants.filter((v) => v.name.trim()).map((v) => ({ id: v.id, name: v.name.trim(), priceMinor: parseMoneyToMinor(v.price, currency) ?? 0 })),
    };
    try {
      if (item) {
        await api.patch(`/items/${item.id}`, {
          ...body,
          description: form.description.trim() || null,
          isVisible: form.isVisible,
          categoryId: form.categoryId !== item.categoryId ? form.categoryId : undefined,
          taxRateBps: form.taxRate.trim() ? Math.round(Number(form.taxRate) * 100) : null,
          ...(markReviewed ? { reviewed: true, allergensReviewed: true } : {}),
        });
      } else {
        await api.post(`/categories/${form.categoryId}/items`, body);
      }
      await invalidate();
      toast.success(item ? "Item saved" : "Item added");
      onClose();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()} title={item ? "Edit item" : "New item"}>
      <div className="grid gap-4">
        {item?.needsReview || item?.allergensReviewed === false ? (
          <div className="rounded-lg border border-warning/50 bg-warning/10 p-3 text-sm">
            <div className="flex items-center gap-1.5 font-medium">
              <CircleAlert className="size-4" /> Needs review before publishing
            </div>
            <ul className="mt-1 list-disc pl-5 text-muted-foreground">
              {item.reviewNotes.map((n) => (
                <li key={n}>{n}</li>
              ))}
              {!item.allergensReviewed ? <li>Confirm the allergens below are correct.</li> : null}
            </ul>
          </div>
        ) : null}
        <Field label="Name" htmlFor="iname">
          <Input id="iname" value={form.name} onChange={(e) => set({ name: e.target.value })} autoFocus />
        </Field>
        <Field label="Description" htmlFor="idesc">
          <Textarea id="idesc" value={form.description} onChange={(e) => set({ description: e.target.value })} rows={3} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Price (${currency})`} htmlFor="iprice" error={priceInvalid ? "Enter a price like 12.50" : undefined} hint={form.variants.length ? "Optional when sizes have prices" : undefined}>
            <Input id="iprice" inputMode="decimal" value={form.price} onChange={(e) => set({ price: e.target.value })} placeholder="0.00" aria-invalid={priceInvalid} />
          </Field>
          <Field label="Category" htmlFor="icat">
            <NativeSelect id="icat" value={form.categoryId} onChange={(e) => set({ categoryId: e.target.value })}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </div>
        <div className="grid gap-2">
          <div className="flex items-center justify-between">
            <Label>Sizes / variants</Label>
            <Button variant="ghost" size="sm" onClick={() => set({ variants: [...form.variants, { name: "", price: "" }] })}>
              <Plus /> Add size
            </Button>
          </div>
          {form.variants.map((v, i) => (
            <div key={i} className="flex gap-2">
              <Input placeholder="e.g. 0.5 l" value={v.name} onChange={(e) => set({ variants: form.variants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
              <Input className="w-28" inputMode="decimal" placeholder="0.00" value={v.price} onChange={(e) => set({ variants: form.variants.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)) })} />
              <Button variant="ghost" size="icon-sm" onClick={() => set({ variants: form.variants.filter((_, j) => j !== i) })} aria-label="Remove size">
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
        <div className="grid gap-2">
          <Label>Photo</Label>
          <div className="flex items-center gap-3">
            <div className="flex size-20 items-center justify-center overflow-hidden rounded-lg border bg-muted">{form.imageUrl ? <img src={form.imageUrl} alt="" className="size-full object-cover" /> : <ImageIcon className="size-6 text-muted-foreground" />}</div>
            <div className="flex flex-col gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const p = await picker.pick({ multiple: false });
                  if (p?.[0]) set({ imageAssetId: p[0].id, imageUrl: p[0].thumbUrl ?? p[0].url });
                }}
              >
                {form.imageUrl ? "Replace" : "Choose photo"}
              </Button>
              {form.imageUrl ? (
                <Button variant="ghost" size="sm" onClick={() => set({ imageAssetId: null, imageUrl: null })}>
                  Remove
                </Button>
              ) : null}
            </div>
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Allergens</Label>
          <p className="text-xs text-muted-foreground">Only you can set allergens — Dishfolio never guesses them. Guests see the EU allergen numbers.</p>
          <div className="grid grid-cols-2 gap-1.5">
            {ALLERGENS.map((a) => (
              <label key={a} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox checked={form.allergens.includes(a)} onCheckedChange={(v) => set({ allergens: v ? [...form.allergens, a] : form.allergens.filter((x) => x !== a) })} />
                {allergenLabel("en", a)}
              </label>
            ))}
          </div>
        </div>
        <div className="grid gap-2">
          <Label>Labels</Label>
          <div className="flex flex-wrap gap-1.5">
            {DIETARY_TAGS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => set({ dietaryTags: form.dietaryTags.includes(t) ? form.dietaryTags.filter((x) => x !== t) : [...form.dietaryTags, t] })}
                className={cn("cursor-pointer rounded-full border px-2.5 py-1 text-xs", form.dietaryTags.includes(t) && "border-primary bg-primary/10 text-primary")}
              >
                {dietaryLabel("en", t)}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-3 rounded-lg border p-3">
          <label className="flex items-center justify-between text-sm">
            <span>
              Available now
              <span className="block text-xs text-muted-foreground">Turn off when sold out — updates the live menu instantly.</span>
            </span>
            <Switch checked={form.isAvailable} onCheckedChange={(v) => set({ isAvailable: v })} />
          </label>
          {item ? (
            <label className="flex items-center justify-between text-sm">
              <span>Show on menu</span>
              <Switch checked={form.isVisible} onCheckedChange={(v) => set({ isVisible: v })} />
            </label>
          ) : null}
          {item ? (
            <Field label="Tax rate override (%)" hint="Leave empty to use the restaurant’s rate.">
              <Input inputMode="decimal" value={form.taxRate} onChange={(e) => set({ taxRate: e.target.value })} />
            </Field>
          ) : null}
        </div>
        {item ? <ModifierGroupsEditor item={item} onChanged={() => void invalidate()} /> : null}
        <div className="sticky bottom-0 -mx-6 flex justify-end gap-2 border-t bg-card px-6 py-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {item && (item.needsReview || !item.allergensReviewed) ? (
            <Button variant="outline" onClick={() => save(true)} loading={saving} disabled={!form.name.trim() || priceInvalid}>
              Save & mark reviewed
            </Button>
          ) : null}
          <Button onClick={() => save(false)} loading={saving} disabled={!form.name.trim() || priceInvalid}>
            Save
          </Button>
        </div>
      </div>
      {picker.element}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Category & item lists
// ---------------------------------------------------------------------------

function SortableRow({ id, children, disabled }: { id: string; children: (handle: React.ReactNode) => React.ReactNode; disabled?: boolean }) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id, disabled });
  const handle = (
    <button type="button" className="cursor-grab touch-none text-muted-foreground hover:text-foreground disabled:cursor-default" {...attributes} {...listeners} disabled={disabled} aria-label="Drag to reorder">
      <GripVertical className="size-4" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Translate.toString(transform), transition }} className={cn(isDragging && "relative z-10 opacity-80")}>
      {children(handle)}
    </div>
  );
}

function ItemRow({ item, currency, handle, onEdit, canEdit, menuId }: { item: ItemDto; currency: string; handle: React.ReactNode; onEdit: () => void; canEdit: boolean; menuId: string }) {
  const invalidate = useInvalidateMenu(menuId);
  const { can } = useOrg();
  const [confirm, setConfirm] = useState(false);
  const price = item.priceMinor != null ? money(item.priceMinor, currency) : item.variants.length ? item.variants.map((v) => `${v.name} ${money(v.priceMinor, currency)}`).join(" · ") : "No price";
  return (
    <div className={cn("flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5", !item.isVisible && "opacity-60")}>
      {canEdit ? handle : null}
      <div className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">{item.imageUrl ? <img src={item.imageUrl} alt="" className="size-full object-cover" /> : <ImageIcon className="size-4 text-muted-foreground" />}</div>
      <button type="button" className="min-w-0 flex-1 cursor-pointer text-left" onClick={onEdit}>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate font-medium">{item.name}</span>
          {item.needsReview || !item.allergensReviewed ? (
            <Badge variant="warning">
              <CircleAlert /> Review
            </Badge>
          ) : null}
          {!item.isAvailable ? <Badge variant="outline">Sold out</Badge> : null}
          {!item.isVisible ? (
            <Badge variant="outline">
              <EyeOff /> Hidden
            </Badge>
          ) : null}
          {item.allergens.length ? <span className="text-xs text-muted-foreground">· {item.allergens.length} allergens</span> : null}
        </div>
        {item.description ? <div className="truncate text-sm text-muted-foreground">{item.description}</div> : null}
      </button>
      <div className={cn("shrink-0 text-sm font-medium", item.priceMinor == null && !item.variants.length && "text-destructive")}>{price}</div>
      {can("orders:manage") || canEdit ? (
        <Switch
          checked={item.isAvailable}
          aria-label="Available"
          onCheckedChange={async (v) => {
            try {
              await api.patch(`/items/${item.id}`, { isAvailable: v });
              await invalidate();
            } catch (err) {
              notifyError(err);
            }
          }}
        />
      ) : null}
      {canEdit ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Item actions">
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={async () => {
                await api.post(`/items/${item.id}/duplicate`);
                await invalidate();
              }}
            >
              <Copy /> Duplicate
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={async () => {
                await api.patch(`/items/${item.id}`, { isVisible: !item.isVisible });
                await invalidate();
              }}
            >
              {item.isVisible ? <EyeOff /> : <Eye />} {item.isVisible ? "Hide from menu" : "Show on menu"}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={() => setConfirm(true)}>
              <Trash2 /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Delete “${item.name}”?`}
        description="It will disappear from the menu after you publish. Past orders keep their details."
        destructive
        confirmLabel="Delete"
        onConfirm={async () => {
          await api.del(`/items/${item.id}`);
          await invalidate();
        }}
      />
    </div>
  );
}

function CategoryCard({ category, menuId, currency, categories, handle, canEdit }: { category: CategoryDto; menuId: string; currency: string; categories: CategoryDto[]; handle: React.ReactNode; canEdit: boolean }) {
  const invalidate = useInvalidateMenu(menuId);
  const [editing, setEditing] = useState<ItemDto | "new" | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description ?? "");
  const [confirm, setConfirm] = useState(false);
  const [items, setItems] = useState(category.items);
  useEffect(() => setItems(category.items), [category.items]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const next = arrayMove(items, items.findIndex((i) => i.id === e.active.id), items.findIndex((i) => i.id === e.over!.id));
    setItems(next);
    try {
      await api.post(`/categories/${category.id}/items/reorder`, { ids: next.map((i) => i.id) });
      await invalidate();
    } catch (err) {
      notifyError(err);
    }
  };
  return (
    <Card className={cn("gap-3", !category.isVisible && "opacity-70")}>
      <div className="flex items-start gap-2">
        {canEdit ? <div className="pt-1">{handle}</div> : null}
        <div className="min-w-0 flex-1">
          {renaming ? (
            <form
              className="grid gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                await api.patch(`/categories/${category.id}`, { name, description: description || null });
                setRenaming(false);
                await invalidate();
              }}
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (optional)" />
              <div className="flex gap-2">
                <Button size="sm" type="submit">
                  Save
                </Button>
                <Button size="sm" variant="ghost" type="button" onClick={() => setRenaming(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <>
              <h3 className="flex items-center gap-2 font-semibold">
                {category.name}
                {!category.isVisible ? <Badge variant="outline">Hidden</Badge> : null}
                <span className="text-xs font-normal text-muted-foreground">{category.items.length} items</span>
              </h3>
              {category.description ? <p className="text-sm text-muted-foreground">{category.description}</p> : null}
            </>
          )}
        </div>
        {canEdit ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Category actions">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setRenaming(true)}>
                <Pencil /> Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={async () => {
                  await api.patch(`/categories/${category.id}`, { isVisible: !category.isVisible });
                  await invalidate();
                }}
              >
                {category.isVisible ? <EyeOff /> : <Eye />} {category.isVisible ? "Hide category" : "Show category"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={() => setConfirm(true)}>
                <Trash2 /> Delete category
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="grid gap-2">
            {items.map((item) => (
              <SortableRow key={item.id} id={item.id} disabled={!canEdit}>
                {(h) => <ItemRow item={item} currency={currency} handle={h} onEdit={() => canEdit && setEditing(item)} canEdit={canEdit} menuId={menuId} />}
              </SortableRow>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {canEdit ? (
        <Button variant="outline" size="sm" className="justify-self-start" onClick={() => setEditing("new")}>
          <Plus /> Add item
        </Button>
      ) : null}
      {editing ? <ItemSheet menuId={menuId} item={editing === "new" ? null : editing} categoryId={category.id} categories={categories} currency={currency} onClose={() => setEditing(null)} /> : null}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Delete “${category.name}” and its ${category.items.length} items?`}
        destructive
        confirmLabel="Delete"
        onConfirm={async () => {
          await api.del(`/categories/${category.id}`);
          await invalidate();
        }}
      />
    </Card>
  );
}

export function MenuContentPage() {
  const { menuId } = useParams({ from: "/app/menus/$menuId/" });
  const content = useContent(menuId);
  const menu = useMenu(menuId);
  const restaurants = useRestaurants();
  const { can } = useOrg();
  const invalidate = useInvalidateMenu(menuId);
  const [newCat, setNewCat] = useState("");
  const [order, setOrder] = useState<CategoryDto[] | null>(null);
  const cats = order ?? content.data ?? [];
  useEffect(() => setOrder(null), [content.data]);
  const currency = restaurants.data?.find((r) => r.id === menu.data?.menu.restaurantId)?.currency ?? "EUR";
  const canEdit = can("menu:edit") && !menu.data?.frozenReason;
  const reviewCount = (content.data ?? []).reduce((n, c) => n + c.items.filter((i) => i.needsReview || !i.allergensReviewed).length, 0);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const addCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await api.post(`/menus/${menuId}/categories`, { name: newCat.trim() });
      setNewCat("");
      await invalidate();
    } catch (err) {
      notifyError(err);
    }
  };
  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const next = arrayMove(cats, cats.findIndex((c) => c.id === e.active.id), cats.findIndex((c) => c.id === e.over!.id));
    setOrder(next);
    try {
      await api.post(`/menus/${menuId}/categories/reorder`, { ids: next.map((c) => c.id) });
      await invalidate();
    } catch (err) {
      notifyError(err);
    }
  };
  const reviewAll = async () => {
    const ids = (content.data ?? []).flatMap((c) => c.items.filter((i) => i.needsReview || !i.allergensReviewed).map((i) => i.id));
    await api.post(`/menus/${menuId}/items/review`, { ids });
    await invalidate();
    toast.success(`${ids.length} items marked as reviewed`);
  };
  if (content.isPending) {
    return (
      <PageContainer wide>
        <div className="grid gap-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </PageContainer>
    );
  }
  return (
    <PageContainer wide>
      {reviewCount > 0 ? (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-warning/50 bg-warning/10 p-4 text-sm">
          <CircleAlert className="size-5" />
          <div className="flex-1">
            <div className="font-medium">{reviewCount} items need review before publishing</div>
            <p className="text-muted-foreground">Check prices, currency, sizes and allergen information marked during import. Open each item, or confirm all at once if you’ve checked them.</p>
          </div>
          {canEdit ? (
            <Button size="sm" variant="outline" onClick={reviewAll}>
              I’ve checked them all
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className="grid gap-4">
        {cats.length === 0 ? (
          <EmptyState title="No categories yet" description="Start with a category like “Starters”, then add dishes with prices." />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={cats.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {cats.map((c) => (
                <SortableRow key={c.id} id={c.id} disabled={!canEdit}>
                  {(h) => <CategoryCard category={c} menuId={menuId} currency={currency} categories={cats} handle={h} canEdit={canEdit} />}
                </SortableRow>
              ))}
            </SortableContext>
          </DndContext>
        )}
        {canEdit ? (
          <form onSubmit={addCategory} className="flex gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name, e.g. Desserts" className="max-w-sm" />
            <Button type="submit" variant="outline" disabled={!newCat.trim()}>
              <Plus /> Add category
            </Button>
          </form>
        ) : null}
      </div>
    </PageContainer>
  );
}
